import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Badge,
  Center,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  Spacer,
  Flex,
} from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';
import { usePurchases } from '../hooks/usePurchases';
import { getTeamNameById } from '../utils/getTeamInfo';
import { formatSalary } from '../utils/formatMoney';

type Chip = { type: 'type' | 'team'; label: string; value?: string };

type PurchaseItem = {
  name?: string;
  item_id?: number;
  created_at?: string;
  createdAt?: string;
};

type PurchaseRecord = {
  id: number;
  team_id?: number;
  items?: PurchaseItem[];
  user?: { display_name?: string; email?: string };
  total_cents?: number | null;
  created_at?: string;
  status?: string;
};

const typeOptions = [
  { label: 'Team Ownership', values: ['Team Ownership'] },
  { label: 'Contract Extension', values: ['Contract Extension'] },
  { label: 'Media (Share/Split)', values: ['Media Share', 'Media Split'] },
  { label: 'Salary Cap', values: ['Salary Cap'] },
  { label: 'Franchise Tag', values: ['Franchise Tag'] },
  { label: 'Arbitration Master', values: ['Arbitration Master Negotiator'] },
];

const Purchases: React.FC = () => {
  const [selectedTypeLabels, setSelectedTypeLabels] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [accumulatedPurchases, setAccumulatedPurchases] = useState<PurchaseRecord[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [typePopoverOpen, setTypePopoverOpen] = useState(false);
  const [teamPopoverOpen, setTeamPopoverOpen] = useState(false);

  const myLeague = useMyLeagueStore(state => state.myLeague);
  const leagueId = myLeague?.id;
  const { myTeam } = useUserContext();

  const filterKey = useMemo(
    () => JSON.stringify({ selectedTypeLabels, selectedTeamIds, showAllTeams }),
    [selectedTypeLabels, selectedTeamIds, showAllTeams]
  );

  useEffect(() => {
    setCurrentPage(0);
    setAccumulatedPurchases([]);
    setTotalPages(0);
  }, [filterKey]);

  const selectedTypeValues = useMemo(() => {
    return typeOptions
      .filter(opt => selectedTypeLabels.includes(opt.label))
      .flatMap(opt => opt.values);
  }, [selectedTypeLabels]);

  const purchaseFilters = useMemo(() => ({
    type: selectedTypeValues.length > 0 ? selectedTypeValues : undefined,
    teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
    myTeam: !showAllTeams && myTeam ? Number(myTeam) : undefined,
  }), [selectedTypeValues, selectedTeamIds, myTeam, showAllTeams]);

  const {
    data,
    isLoading,
    error,
    isFetching,
  } = usePurchases({
    leagueId: leagueId || 0,
    filters: purchaseFilters,
    page: currentPage,
    pageSize: 25,
  });

  useEffect(() => {
    if (!data) return;
    const purchases = (data.purchases || []).filter((purchase: PurchaseRecord) => purchase.status !== 'created');
    setTotalPages(data.totalPages || 0);
    if (currentPage === 0) {
      setAccumulatedPurchases(purchases);
    } else if (purchases.length > 0) {
      setAccumulatedPurchases(prev => [...prev, ...purchases]);
    }
  }, [data, currentPage]);

  const chips: Chip[] = [
    ...selectedTypeLabels.map(label => ({ type: 'type' as const, label, value: label })),
    ...selectedTeamIds.map(teamId => ({
      type: 'team' as const,
      label: getTeamNameById(teamId, myLeague) || `Team ${teamId}`,
      value: teamId.toString(),
    })),
  ];

  const removeChip = (type: Chip['type'], value?: string) => {
    if (type === 'type' && value) {
      setSelectedTypeLabels(prev => prev.filter(label => label !== value));
    }
    if (type === 'team' && value) {
      const id = Number(value);
      setSelectedTeamIds(prev => prev.filter(teamId => teamId !== id));
    }
  };

  const handleToggleType = (label: string) => {
    setSelectedTypeLabels(prev =>
      prev.includes(label) ? prev.filter(item => item !== label) : [...prev, label]
    );
  };

  const handleToggleTeam = (teamId: number) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const allTeams = useMemo(() => {
    if (!myLeague?.conferences) return [];
    const teams: Array<{ id: number; name: string }> = [];
    myLeague.conferences.forEach(conf => {
      conf.divisions.forEach(div => {
        div.teams.forEach(team => {
          teams.push({ id: team.id, name: team.name });
        });
      });
    });
    return teams.sort((a, b) => a.name.localeCompare(b.name));
  }, [myLeague]);

  const handleShowMore = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const formatAmount = (totalCents: number | null | undefined) => {
    if (totalCents == null) return '-';
    return formatSalary(totalCents / 100);
  };

  const formatDateOnly = (rawDate: string | null | undefined) => {
    if (!rawDate) return '-';
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  const getPurchaseDate = (purchase?: PurchaseRecord) => {
    const items = purchase?.items;
    if (Array.isArray(items)) {
      const itemWithDate = items.find((item: PurchaseItem) => item.created_at || item.createdAt);
      if (itemWithDate) {
        return itemWithDate.created_at || itemWithDate.createdAt;
      }
    }
    return purchase?.created_at;
  };

  const isPendingStatus = (status?: string): boolean => {
    if (!status) return false;
    const pendingStatuses = ['pending', 'processing', 'requires_capture', 'requires_action', 'requires_payment_method'];
    return pendingStatuses.includes(status.toLowerCase());
  };

  return (
    <Box className="minimal-panel-padding">
      <PageTitle mb={4}>Purchases</PageTitle>
      <Flex align="center" justify="space-between" mb={2} flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Popover
            isOpen={typePopoverOpen}
            onClose={() => setTypePopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setTypePopoverOpen(prev => !prev)} variant="outline" colorScheme="blue" size="sm">
                {selectedTypeLabels.length > 0 ? `${selectedTypeLabels.length} Type${selectedTypeLabels.length > 1 ? 's' : ''}` : 'Select Types'}
              </Button>
            </PopoverTrigger>
          <PopoverContent bg="gray.800" color="white" minW="220px">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              <VStack align="flex-start" spacing={1}>
                {typeOptions.map(option => (
                  <Checkbox
                    key={option.label}
                    isChecked={selectedTypeLabels.includes(option.label)}
                    onChange={() => handleToggleType(option.label)}
                    colorScheme="blue"
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        {showAllTeams && (
          <Popover
            isOpen={teamPopoverOpen}
            onClose={() => setTeamPopoverOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button onClick={() => setTeamPopoverOpen(prev => !prev)} variant="outline" colorScheme="blue" size="sm">
                {selectedTeamIds.length > 0 ? `${selectedTeamIds.length} Team${selectedTeamIds.length > 1 ? 's' : ''}` : 'Select Teams'}
              </Button>
            </PopoverTrigger>
            <PopoverContent bg="gray.800" color="white" minW="220px" maxH="320px" overflowY="auto">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <VStack align="flex-start" spacing={1}>
                  {allTeams.map(team => (
                    <Checkbox
                      key={team.id}
                      isChecked={selectedTeamIds.includes(team.id)}
                      onChange={() => handleToggleTeam(team.id)}
                      colorScheme="blue"
                    >
                      {team.name}
                    </Checkbox>
                  ))}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
        </HStack>
        <HStack spacing={2} flexWrap="wrap" align="center" display={{ base: 'none', md: 'flex' }}>
          <Text color="white" fontSize="sm" textAlign="right" lineHeight="1.2">
            My<br />Team
          </Text>
          <Switch
            colorScheme="green"
            isChecked={showAllTeams}
            isDisabled={!myTeam}
            onChange={(event) => setShowAllTeams(event.target.checked)}
            sx={{
              '& .chakra-switch__track': {
                bg: '#22a000',
                _checked: {
                  bg: '#22a000',
                },
              },
              '& .chakra-switch__thumb': {
                bg: 'white',
              },
            }}
          />
          <Text color="gray.300" fontSize="sm" textAlign="left" lineHeight="1.2">
            All<br />Teams
          </Text>
        </HStack>
      </Flex>
      <HStack spacing={2} flexWrap="wrap" align="center" mb={2} display={{ base: 'flex', md: 'none' }}>
        <Text color="white" fontSize="sm" textAlign="right" lineHeight="1.2">
          My<br />Team
        </Text>
        <Switch
          colorScheme="green"
          isChecked={showAllTeams}
          isDisabled={!myTeam}
          onChange={(event) => setShowAllTeams(event.target.checked)}
          sx={{
            '& .chakra-switch__track': {
              bg: '#22a000',
              _checked: {
                bg: '#22a000',
              },
            },
            '& .chakra-switch__thumb': {
              bg: 'white',
            },
          }}
        />
        <Text color="gray.300" fontSize="sm" textAlign="left" lineHeight="1.2">
          All<br />Teams
        </Text>
      </HStack>
      <HStack spacing={2} flexWrap="wrap" mb={4}>
        {chips.map((chip, idx) => (
          <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
            <TagLabel>{chip.label}</TagLabel>
            <TagCloseButton onClick={() => removeChip(chip.type, chip.value)} />
          </Tag>
        ))}
      </HStack>

      <Box mt={4}>
        {isLoading && currentPage === 0 ? (
          <Center py={10}><Spinner size="xl" /></Center>
        ) : error ? (
          <Text color="red.400">Error loading purchases.</Text>
        ) : (
          <>
            <Box width="100%" overflowX="auto" className="tight-table-padding">
              <Table variant="simple" size="sm" bg="whiteAlpha.100" borderRadius="md" minW="560px">
              <Thead>
                <Tr>
              <Th color="white" w={{ base: '140px', md: '140px' }} minW="140px" maxW="140px">Date</Th>
              <Th color="white" display={{ base: 'none', md: 'table-cell' }}>Team</Th>
                  <Th color="white">Items</Th>
                  {showAllTeams ? null : (
                    <>
                      <Th color="white">Purchaser</Th>
                      <Th color="white">Amount</Th>
                    </>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {accumulatedPurchases.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" color="gray.400">No purchases found.</Td>
                  </Tr>
                ) : (
                  accumulatedPurchases.map((purchase: PurchaseRecord) => {
                    const teamName = getTeamNameById(purchase.team_id, myLeague) || '-';
                    const purchaseItems = Array.isArray(purchase.items) ? purchase.items : [];
                    return (
                      <Tr key={purchase.id}>
                        <Td color="white" w={{ base: '140px', md: '140px' }} minW="140px" maxW="140px">
                          <Box display={{ base: 'block', md: 'none' }} mb={1}>
                            <Text fontSize="sm" color="gray.400">
                              {teamName}
                            </Text>
                          </Box>
                          {formatDateOnly(getPurchaseDate(purchase))}
                        </Td>
                        <Td color="white" display={{ base: 'none', md: 'table-cell' }}>
                          {teamName}
                        </Td>
                        <Td color="white">
                          {purchaseItems.length > 0 ? (
                            <VStack align="flex-start" spacing={1}>
                              {purchaseItems.map((item: PurchaseItem, index: number) => (
                                <Text key={index} fontSize="sm">
                                  {item.displayItem || item.name || `Item ${item.item_id}`}
                                </Text>
                              ))}
                            </VStack>
                          ) : (
                            '-'
                          )}
                        </Td>
                        {showAllTeams ? null : (
                          <>
                            <Td color="white">{purchase.user?.display_name || purchase.user?.email || '-'}</Td>
                            <Td color="white">
                              <HStack spacing={2} align="center">
                                <Text>{formatAmount(purchase.total_cents)}</Text>
                                {isPendingStatus(purchase.status) && (
                                  <Badge colorScheme="yellow" variant="solid" fontSize="xs">
                                    Pending
                                  </Badge>
                                )}
                              </HStack>
                            </Td>
                          </>
                        )}
                      </Tr>
                    );
                  })
                )}
              </Tbody>
              </Table>
            </Box>
            {totalPages > 0 && currentPage < totalPages - 1 && (
              <Center py={6}>
                <Button
                  onClick={handleShowMore}
                  isLoading={isFetching}
                  loadingText="Loading..."
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                >
                  Show More
                </Button>
              </Center>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Purchases;

